/**
 * useObjectivesPoller — polls the backend for pedagogical objectives targeted
 * at the current apprentice.
 *   - Active objectives: surfaced to the in-game Dashboard via state slice
 *     `pedagogicalObjectives` (read by ObjectivesWidget).
 *   - Achieved/Failed objectives still pending-ack: pushed into the
 *     Messagerie as system messages, then ack-ed so they don't re-trigger.
 *
 * Polling cadence: on mount + month change + 5-min fallback.
 *
 * The backend evaluates objectives server-side whenever a fresh telemetry
 * snapshot is upserted, so this hook only needs to read state — no need to
 * push current values from the client.
 */
import { useEffect, useRef } from 'react';
import useGameStore from '../store/gameStore';
import useLicenseStore from '../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_INTERVAL_MS = 5 * 60 * 1000;

async function fetchObjectives(licenseKey) {
  if (!licenseKey || !BACKEND) return [];
  try {
    const resp = await fetch(
      `${BACKEND}/api/master/objectives/by-apprentice?license_key=${encodeURIComponent(licenseKey)}`
    );
    if (!resp.ok) return [];
    const data = await resp.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch (_) { return []; }
}

async function ackObjective(id, licenseKey) {
  if (!id || !licenseKey || !BACKEND) return;
  try {
    await fetch(`${BACKEND}/api/master/objectives/${encodeURIComponent(id)}/ack?license_key=${encodeURIComponent(licenseKey)}`, {
      method: 'POST',
      keepalive: true,
    });
  } catch (_) { /* silent */ }
}

const OUTCOME_TO_SUBJECT = {
  achieved: 'Objectif atteint',
  failed:   'Objectif manqué',
};

const OUTCOME_TO_TONE = {
  achieved: 'friendly',
  failed:   'formal',
};

const OUTCOME_TO_PREFIX = {
  achieved: 'Bravo ! Vous avez atteint l\'objectif fixé par votre Responsable Pédagogique :',
  failed:   'L\'objectif fixé par votre Responsable Pédagogique n\'a pas été atteint dans le délai imparti :',
};

const OUTCOME_TO_SUFFIX = {
  achieved: 'Votre Responsable est automatiquement notifié.',
  failed:   'Pas grave — chaque difficulté est une occasion d\'apprendre. Continuez !',
};

async function drainAndPushOutcomes(licenseKey, items) {
  const state = useGameStore.getState();
  if (!state.gameStarted || !state.pushCustomMessage) return;
  for (const o of items) {
    if ((o.status === 'achieved' || o.status === 'failed') && !o.ack_at) {
      const sub = OUTCOME_TO_SUBJECT[o.status] || 'Objectif mis à jour';
      const tone = OUTCOME_TO_TONE[o.status] || 'formal';
      const prefix = OUTCOME_TO_PREFIX[o.status] || '';
      const suffix = OUTCOME_TO_SUFFIX[o.status] || '';
      const body = `${prefix}\n\n« ${o.title} »\n(${o.metric_label || o.metric} ${o.operator} ${o.threshold})\n\n${suffix}`;
      state.pushCustomMessage({
        senderType: 'supervisor',
        senderName: o.master_holder || 'Responsable pédagogique',
        senderId: o.master_id || null,
        tone,
        kind: 'info',
        subject: `${sub} — « ${o.title} »`,
        body,
        ttlDays: 21,
        payload: { supervisorObjectiveId: o.id, outcome: o.status },
      });
      await ackObjective(o.id, licenseKey);
    }
  }
}

export default function useObjectivesPoller() {
  const licenseKey = useLicenseStore(s => s.licenseKey);
  const monthRef = useRef(null);
  const startedRef = useRef(false);

  const tick = async () => {
    if (!licenseKey) return;
    const items = await fetchObjectives(licenseKey);
    // Cache active objectives into the game state for the Dashboard widget.
    const active = items.filter(o => o.status === 'active');
    const state = useGameStore.getState();
    if (state.setPedagogicalObjectives) {
      state.setPedagogicalObjectives(active);
    }
    // Push outcome messages for newly achieved/failed objectives.
    await drainAndPushOutcomes(licenseKey, items);
  };

  // Effect 1: initial + month-change.
  useEffect(() => {
    if (!licenseKey) return undefined;
    const initial = useGameStore.getState();
    if (initial.gameStarted && !startedRef.current) {
      startedRef.current = true;
      monthRef.current = `${initial.year}-${initial.month}`;
      tick();
    }
    const unsubscribe = useGameStore.subscribe((state) => {
      const tag = `${state.year}-${state.month}`;
      if (state.gameStarted && !startedRef.current) {
        startedRef.current = true;
        monthRef.current = tag;
        tick();
        return;
      }
      if (state.gameStarted && monthRef.current && tag !== monthRef.current) {
        monthRef.current = tag;
        tick();
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseKey]);

  // Effect 2: 5-min fallback.
  useEffect(() => {
    if (!licenseKey) return undefined;
    const id = setInterval(() => {
      const state = useGameStore.getState();
      if (state.gameStarted) tick();
    }, FALLBACK_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseKey]);
}
